import { CheckIcon } from "@phosphor-icons/react/dist/ssr";
import { Link } from "zudoku/components";
import { cn } from "zudoku/ui/util";

export interface WizardProgressProps {
  phase: 1 | 2 | 3;
  skipMethod?: boolean;
  buildLabel?: string;
  buildHref?: string;
  methodLabel?: string;
  methodHref?: string;
  tutorialLabel?: string;
}

export function WizardProgress({
  phase,
  skipMethod = false,
  buildLabel,
  buildHref,
  methodLabel,
  methodHref,
  tutorialLabel,
}: WizardProgressProps) {
  const steps = [
    {
      n: 1,
      title: "Choose a product",
      label: buildLabel ?? "What you want to build",
      href: buildHref,
    },
    {
      n: 2,
      title: "Choose a workflow",
      label: methodLabel ?? "How you want to work",
      href: methodHref,
    },
    {
      n: 3,
      title: "Build and test",
      label: tutorialLabel ?? "Follow your tutorial",
      href: undefined,
    },
  ].filter((step) => !skipMethod || step.n !== 2);

  return (
    <nav
      aria-label="Getting started progress"
      className="not-prose mb-8 max-w-6xl"
      style={{ viewTransitionName: "wizard-progress" }}
    >
      <ol
        className={cn(
          "m-0 grid list-none gap-3 p-0 sm:gap-6",
          skipMethod ? "grid-cols-2" : "grid-cols-3",
        )}
      >
        {steps.map(({ n, title, label, href }, index) => {
          const active = n === phase;
          const done = n < phase;
          const content = (
            <>
              <span
                aria-hidden="true"
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs tabular-nums",
                  active
                    ? "border-foreground bg-foreground text-background"
                    : "border-border text-muted-foreground",
                )}
              >
                {done ? <CheckIcon size={13} /> : index + 1}
              </span>
              <span className="min-w-0">
                <span
                  className={cn(
                    "block text-xs font-semibold leading-relaxed sm:text-sm",
                    active || done
                      ? "text-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  {title}
                </span>
                <span className="mt-1 hidden text-xs leading-relaxed text-muted-foreground sm:block">
                  {label}
                </span>
              </span>
            </>
          );
          const className = cn(
            "flex h-full flex-col gap-2 border-b-2 pb-4 pt-1 no-underline sm:flex-row sm:gap-3",
            active ? "border-primary" : "border-border",
          );
          return (
            <li key={n} aria-current={active ? "step" : undefined}>
              {done && href ? (
                <Link
                  to={href}
                  viewTransition
                  className={cn(
                    className,
                    "rounded-t-sm transition-colors hover:border-foreground/40 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring",
                  )}
                >
                  {content}
                </Link>
              ) : (
                <span className={className}>{content}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
